import React, { UIEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { Badge, Group, px, Text, useMantineTheme } from '@mantine/core';
import { IconArchive, IconCopy, IconEdit, IconEye, IconPlus, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import PackageTypeLabel from 'components/PackageTypeLabel';
import useClonePackage from 'hooks/package/useClonePackage';
import usePackages from 'hooks/package/usePackages';
import { MantineReactTable, MRT_Row, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table';
import { PackageListView } from 'types';
import notificationHandler from 'utils/notifications';

import PackageDetailPanel from './components/PackageDetailPanel';
import PackageDrawerState, { PackageType } from '../PackageDrawer/types';

interface IPackageTableContainerProps {
  search: string;
  eventTypes: string[];
  packageCategory?: string;
  revenueCategories?: string[];
  packageTypes?: PackageListView['type'][];
  isArchived: boolean;
  openPackageDrawer: (packageId: string, state: PackageDrawerState, packageType?: PackageType) => void;
  openRevenueCategorizationDrawer: (packageId: string) => void;
  openSalesPortalOptionsDrawer: (packageSalesPortalConfigurationId: string) => void;
  openPackageDeleteModal: (packageId: string) => void;
  openArchivePackageModal: (packageId: string) => void;
  permission?: boolean;
  headerHeight: number;
}

const PackageTableContainer = ({
  search,
  eventTypes,
  packageCategory,
  revenueCategories,
  packageTypes,
  isArchived,
  openPackageDrawer,
  openRevenueCategorizationDrawer,
  openSalesPortalOptionsDrawer,
  openPackageDeleteModal,
  openArchivePackageModal,
  permission = true,
  headerHeight,
}: IPackageTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, isFetching, fetchNextPage } = usePackages({
    search,
    eventTypes,
    packageCategory,
    packageRevenueCategories: revenueCategories,
    types: packageTypes,
    isArchived: isArchived,
    cacheKey: 'package-table',
  });

  const flatData = useMemo(() => data?.pages.flatMap(page => page.data.results) ?? [], [data]);

  const totalPackagesCount = data?.pages?.[0]?.data?.count ?? 0;
  const totalPackagesFetchedCount = flatData.length;
  const clonePackageMutation = useClonePackage();
  const { successNotification, errorNotification } = notificationHandler();

  const handleClonePackage = (packageId: string): void => {
    clonePackageMutation.mutate(packageId, {
      onSuccess: response => {
        successNotification('Package cloned', 'The package has been successfully cloned.');
        openPackageDrawer(response.data.packageId, 'view-package');
      },
      onError: error => {
        errorNotification('Clone failed', error.response?.data.message);
      },
    });
  };

  const getVisibilityBadges = (row: MRT_Row<PackageListView>): JSX.Element => {
    const isUnlisted =
      row.original.athleteSalesPortalStatus === 'DISABLED' && row.original.salesPortalStatus === 'DISABLED';
    return (
      <Group spacing={1}>
        {isUnlisted ? (
          <Badge color='gray'>Unlisted</Badge>
        ) : (
          <>
            {row.original.athleteSalesPortalStatus === 'ENABLED' && <Badge color='green'>App</Badge>}
            {row.original.salesPortalStatus === 'PUBLIC' && <Badge color='green'>Public Portal</Badge>}
            {row.original.salesPortalStatus === 'PRIVATE' && <Badge color='green'>Private Portal</Badge>}
          </>
        )}
      </Group>
    );
  };

  const getReveneueCategoryBadges = (row: MRT_Row<PackageListView>): JSX.Element => {
    return (
      <Group>
        {row.original.revenueCategories.length === 0 ? <Badge color='gray'>Uncategorized</Badge> : null}
        {row.original.revenueCategories.length === 1 ? (
          <Badge color='gray'>{row.original.revenueCategories[0]}</Badge>
        ) : null}
        {row.original.revenueCategories.length > 1 ? (
          <Group>
            <Badge color='gray'>{row.original.revenueCategories[0]}</Badge>
            <Badge color='gray'>+{row.original.revenueCategories.length - 1}</Badge>
          </Group>
        ) : null}
      </Group>
    );
  };

  const columns: MRT_ColumnDef<PackageListView>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Name
        </Text>
      ),
      Cell: ({ row }) => (
        <Group>
          <Text
            fw='bold'
            fs='italic'
            underline
            sx={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => openPackageDrawer(row.original.packageId, 'view-package', row.original.type)}
            data-cy='packageDrawerName'
          >
            {row.original.name}
          </Text>
          {row.original.isArchived && (
            <Badge color='red' variant='filled' ml='xs'>
              Archived
            </Badge>
          )}
        </Group>
      ),
      size: 150,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Description
        </Text>
      ),
      Cell: ({ cell }) => (
        <Text maw='30vw' truncate data-cy='descriptionOfPackage'>
          {cell.getValue<string>()}
        </Text>
      ),
      size: 200,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Type
        </Text>
      ),
      Cell: ({ row }) => <PackageTypeLabel type={row.original.type} short data-cy='typeOfPackage' />,
      size: 120,
    },
    {
      accessorKey: 'activeAthletePackageCount',
      header: 'Active Count',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Active Count
        </Text>
      ),
      size: 100,
    },
    {
      accessorKey: 'billingPlans',
      header: 'Billing Plans',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Billing Plans
        </Text>
      ),
      Cell: ({ cell }) => {
        const numBps = cell.getValue<string[]>().length;
        const cellContent = numBps === 0 ? 'None' : `${numBps} Available`;

        return <div data-cy='numBpsAvailable'>{cellContent}</div>;
      },
      size: 150,
    },
    {
      accessorKey: 'packageCategory',
      header: 'Package Category',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Package Category
        </Text>
      ),
      Cell: ({ row }) =>
        row.original.packageCategory ? (
          <Badge color='blue'>{row.original.packageCategory}</Badge>
        ) : (
          <Badge color='blue'>Uncategorized</Badge>
        ),
    },
    {
      header: 'Visibility',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Visibility
        </Text>
      ),
      Cell: ({ row }) => getVisibilityBadges(row),
    },
    {
      accessorKey: 'revenueCategory',
      header: 'Revenue Category',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Revenue Category
        </Text>
      ),
      Cell: ({ row }) => getReveneueCategoryBadges(row),
    },
    {
      id: 'action',
      header: '',
      Cell: ({ row }) => {
        const items = [
          {
            title: 'View',
            icon: <IconEye size={theme.fontSizes.sm} />,
            onClick: () => openPackageDrawer(row.original.packageId, 'view-package', row.original.type),
          },
          {
            title: 'Revenue Categorization',
            icon: <IconPlus size={theme.fontSizes.sm} />,
            onClick: () => openRevenueCategorizationDrawer(row.original.packageId),
            'data-test': 'revenue-categorization',
          },
          {
            title: 'Sales Portal Options',
            icon: <IconEdit size={theme.fontSizes.sm} />,
            onClick: () => openSalesPortalOptionsDrawer(row.original.packageId),
            disabled: !permission,
          },
          {
            title: 'Clone',
            icon: <IconCopy size={theme.fontSizes.sm} />,
            onClick: () => handleClonePackage(row.original.packageId),
            disabled: !permission,
          },
          {
            title: 'Archive',
            icon: <IconArchive size={theme.fontSizes.sm} />,
            onClick: () => openArchivePackageModal(row.original.packageId),
            disabled: !permission || isArchived,
          },
          {
            title: 'Delete',
            icon: <IconTrashX size={theme.fontSizes.sm} />,
            color: 'red',
            onClick: () => openPackageDeleteModal(row.original.packageId),
            'data-cy': 'deletePackage',
            disabled: !permission,
          },
        ];
        return <Menu position='left' items={items} data-cy='rowButtonPackage' />;
      },
      size: 50,
    },
  ];

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&
          !isFetching &&
          totalPackagesFetchedCount < totalPackagesCount
        ) {
          fetchNextPage();
        }
      }
    },
    [isFetching, totalPackagesFetchedCount, totalPackagesCount, fetchNextPage],
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const table = useMantineReactTable({
    columns,
    data: flatData,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableExpanding: true,
    enableExpandAll: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: false,
    enableRowSelection: false,
    enableSorting: false,
    enableTopToolbar: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    mantineDetailPanelProps: props => {
      if (props.row.getIsExpanded()) {
        return {
          sx: {
            backgroundColor: theme.colors.gray[1],
          },
        };
      }
      return {};
    },
    mantineTableContainerProps: {
      ref: tableContainerRef,
      sx: { height: `calc(100vh - ${headerHeight + px(theme.spacing.lg) * 2 + 49}px)` },
      onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    mantineToolbarAlertBannerProps: {
      color: 'red',
      children: 'Error loading data',
    },
    displayColumnDefOptions: {
      'mrt-row-expand': { size: 10, Header: '' },
    },
    renderDetailPanel: ({ row }) => {
      if (row.getIsExpanded()) {
        return <PackageDetailPanel packageId={row.original.packageId} />;
      }
      return <></>;
    },
    renderBottomToolbarCustomActions: () => (
      <Text fs='italic' c='dimmed' pl='lg'>
        Fetched {totalPackagesFetchedCount} of {totalPackagesCount} total rows
      </Text>
    ),
    initialState: { density: 'xs' },
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
  });

  return <MantineReactTable table={table} />;
};

export default PackageTableContainer;
